import classNames from 'classnames';
import type { FormikProps, FormikValues } from 'formik';
import React, { useState } from 'react';
import { Waypoint } from 'react-waypoint';
import * as styles from './OrganizationOnboardingStep.module.scss';
import Button from '../../components/Button';
import Form, { Submit } from '../../components/Form';

interface OrganizationOnboardingStepProps<Values> {
  children: React.ReactNode;
  nextSection?: (values: Values) => React.ReactNode;
  onPrevious: (() => void) | null;
  onSubmit: (values: Values) => Promise<boolean | void>;
  onNext: () => void;
  subtitle?: string;
  title: string;
  lastStep?: boolean;
  apiErrorMessages?: React.ComponentProps<typeof Form>['apiErrorMessages'];
  apiErrorHandlers?: React.ComponentProps<typeof Form>['apiErrorHandlers'];
}

const OrganizationOnboardingStep = <Values extends FormikValues>({
  children,
  nextSection,
  onPrevious,
  onSubmit,
  onNext,
  subtitle,
  title,
  lastStep,
  ...props
}: Omit<
  React.ComponentProps<typeof Form>,
  'apiErrorMessages' | 'apiErrorHandlers' | 'onSubmit'
> &
  OrganizationOnboardingStepProps<Values>) => {
  const [isFooterStuck, setIsFooterStuck] = useState(false);
  return (
    // @ts-expect-error issue caused by using propTypes in `Form` component
    <Form<Values>
      onSubmit={(values: Values) =>
        onSubmit(values).then((ok = true) => {
          if (ok) {
            onNext();
          }
        })
      }
      {...props}
    >
      {({ values, isValid }: FormikProps<Values>) => (
        <>
          <div className={styles.formContent}>
            <div className={styles.header}>
              <div>
                <h2>{title}</h2>
                <div className={styles.subtitle}>{subtitle}</div>
              </div>
            </div>
            <div>{children}</div>
          </div>
          <div
            className={classNames(styles.footer, {
              [styles.stuck]: isFooterStuck,
            })}
          >
            {onPrevious && (
              <div className={styles.secondary}>
                <Button size="large" onClick={onPrevious}>
                  Previous
                </Button>
              </div>
            )}
            <div className={styles.primary}>
              {nextSection?.(values) ?? (
                <Submit disabled={!isValid} size="large">
                  {lastStep ? 'Finish' : 'Next'}
                </Submit>
              )}
            </div>
          </div>
          <Waypoint
            onPositionChange={({ currentPosition }) =>
              setIsFooterStuck(currentPosition === Waypoint.below)
            }
          />
        </>
      )}
    </Form>
  );
};

export default OrganizationOnboardingStep;
